import { useCurrentSubscription } from '@/hooks/api/usePlatformSubscription'
import { useNavigate } from '@tanstack/react-router'
import { TryOnButton } from '../shared/atoms/TryOnButton'
import { XIcon } from 'lucide-react'
import { Drawer } from '../shared/Drawer'
import { buildTryOnSrcSet, transformSmall } from '@wtf/shared'
import { env } from '@/config/env'
import { replaceImageDomain } from '@/utils/replaceImageDomain'
import { isIOSApp } from '@/utils/platformDetection'

interface TryOnInfoModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function TryOnInfoModal({ open, onOpenChange }: TryOnInfoModalProps) {
    const navigate = useNavigate()
    const { data: subscription } = useCurrentSubscription()

    const originalImageUrl = `${env.R2_ASSETS_URL}/assets/images/try-on--original.jpg`
    const productImageUrl = `${env.R2_ASSETS_URL}/assets/images/try-on--product.jpg`
    const finalImageUrl = `${env.R2_ASSETS_URL}/assets/images/try-on--final.png`

    const handleUpgrade = () => {
        onOpenChange(false)
        navigate({
            to: '/pricing',
            search: {
                reason: 'out_of_credits',
                returnToPath: undefined,
                preselect: undefined,
            },
        })
    }

    return (
        <Drawer
            open={open}
            onOpenChange={onOpenChange}
            triggerContent={undefined}
        >
            <div className="flex flex-col items-center space-y-4 p-7 text-center">
                <div className="flex flex-col gap-11">
                    {subscription &&
                    subscription.tryOnsRemaining !== undefined ? (
                        <p className="text-2xl font-bold">
                            You have {subscription.tryOnsRemaining} remaining
                            try-ons this month. Just click on any product to
                            begin.
                        </p>
                    ) : (
                        <p className="text-2xl font-bold">
                            Try any item on yourself (or a friend). <br />
                            Just click on any product 👀.
                        </p>
                    )}

                    <div className="hidden flex-row items-center justify-center gap-3 p-3 md:flex">
                        <div className="text-16 flex flex-col gap-2 font-bold">
                            <div className="aspect-3/4 h-32 bg-zinc-100">
                                <img
                                    src={replaceImageDomain(
                                        transformSmall(originalImageUrl) ||
                                            originalImageUrl
                                    )}
                                    srcSet={replaceImageDomain(
                                        buildTryOnSrcSet(originalImageUrl, [
                                            'small',
                                            'medium',
                                        ])
                                    )}
                                    sizes="96px"
                                    alt="Original Try On Image"
                                />
                            </div>
                            You
                        </div>
                        <span className="text-3xl"> +</span>
                        <div className="text-16 flex flex-col gap-2 font-bold">
                            <div className="aspect-3/4 h-32 bg-zinc-100">
                                {' '}
                                <img
                                    src={replaceImageDomain(
                                        transformSmall(productImageUrl) ||
                                            productImageUrl
                                    )}
                                    srcSet={replaceImageDomain(
                                        buildTryOnSrcSet(productImageUrl, [
                                            'small',
                                            'medium',
                                        ])
                                    )}
                                    sizes="96px"
                                    alt="Try On Product"
                                />
                            </div>
                            Product
                        </div>
                        <span className="text-3xl"> =</span>
                        <div className="text-16 flex flex-col gap-2 font-bold">
                            <div className="aspect-3/4 h-32 bg-zinc-100">
                                <img
                                    src={replaceImageDomain(
                                        transformSmall(finalImageUrl) ||
                                            finalImageUrl
                                    )}
                                    srcSet={replaceImageDomain(
                                        buildTryOnSrcSet(finalImageUrl, [
                                            'small',
                                            'medium',
                                        ])
                                    )}
                                    sizes="96px"
                                    alt="Final Result"
                                />
                            </div>
                            Result
                        </div>
                    </div>
                </div>
                {/* TEMPORARY: Phase I iOS restriction - Remove in Phase II
                    Hide upgrade CTA on iOS to comply with App Store guidelines
                    See: docs/PHASE_I_TEMPORARY_IOS_RESTRICTIONS.md */}
                {!isIOSApp() && (
                    <div className="mt-4 flex w-full flex-col items-center justify-center gap-3 md:flex-row">
                        <TryOnButton showIcon={false} onClick={handleUpgrade}>
                            <div className="px-4 py-3">Upgrade Plan</div>
                        </TryOnButton>
                        <p className="text-sm text-gray-600">
                            to get more monthly try ons
                        </p>
                    </div>
                )}
            </div>
            <XIcon
                className="absolute top-4 right-4 cursor-pointer"
                onClick={() => onOpenChange(false)}
            />
        </Drawer>
    )
}
